/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * Invisible host that rasterizes the Stats widget sparkline to PNGs on demand.
 *
 * Mounted once near the app root (see `app/App.js`), it keeps two off-screen
 * `ViewShot`-wrapped `WidgetChartCanvas` instances — one forced light, one
 * forced dark — and registers a capture function in `chartCaptureRegistry`. When
 * the refresh orchestrator calls `captureWidgetChart(series)`, the host updates
 * the series, lets React paint, then captures both variants to base64. Rendering
 * off-screen (translated far off the viewport, `pointerEvents="none"`) keeps it
 * invisible without unmounting, so a capture never fights the layout system.
 */

/* global setTimeout */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { View } from 'react-native';

import WidgetChartCanvas, {
    WIDGET_CHART_WIDTH,
    WIDGET_CHART_HEIGHT,
} from './WidgetChartCanvas';
import {
    registerChartCapturer,
    unregisterChartCapturer,
} from '../../helpers/widgets/chartCaptureRegistry';

/**
 * Lazily load `react-native-view-shot`, tolerating its absence. Its native spec
 * runs `TurboModuleRegistry.getEnforcing("RNViewShot")` at module evaluation,
 * which THROWS the instant the pod is missing from the shipped archive — a plain
 * top-level `import` would therefore abort boot (the 3.87.0(4) iOS crash) or, on
 * a partially-linked binary, surface the "undefined is not a function" fallback
 * seen after the splash. Requiring it here (once, guarded) degrades a stale
 * binary to "no widget chart" instead of a crashed launch.
 *
 * `null` = not attempted, `false` = attempted & unavailable, object = loaded.
 * @returns {{ViewShot: React.ComponentType, captureRef: Function}|false}
 */
let viewShotModule = null;
function loadViewShot() {
    if (viewShotModule !== null) {
        return viewShotModule;
    }
    try {
        // eslint-disable-next-line global-require
        const mod = require('react-native-view-shot');
        const ViewShot = mod.default || mod.ViewShot;
        if (typeof ViewShot === 'undefined' || typeof mod.captureRef !== 'function') {
            viewShotModule = false;
        } else {
            viewShotModule = { ViewShot, captureRef: mod.captureRef };
        }
    } catch (_error) {
        viewShotModule = false;
    }
    return viewShotModule;
}

/** Small settle delay so chart-kit's SVG has painted before we snapshot it. */
const SETTLE_MS = 250;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const WidgetChartHost = () => {
    const [series, setSeries] = useState([0, 0]);
    const lightRef = useRef(null);
    const darkRef = useRef(null);

    // Resolve the guarded native module once per mount. `false` when the pod is
    // absent from the shipped binary — the host then renders nothing.
    const viewShot = loadViewShot();

    const capture = useCallback(async (nextSeries) => {
        if (!viewShot) {
            return { light: null, dark: null };
        }
        setSeries(Array.isArray(nextSeries) && nextSeries.length ? nextSeries : [0, 0]);
        // Allow the state update to flush + chart to paint before snapshotting.
        await wait(SETTLE_MS);

        const grab = async (ref) => {
            if (!ref?.current) {
                return null;
            }
            try {
                const base64 = await viewShot.captureRef(ref, {
                    format: 'png',
                    quality: 0.9,
                    result: 'base64',
                });

                return base64 ? `data:image/png;base64,${base64}` : null;
            } catch (_error) {
                return null;
            }
        };

        const [light, dark] = await Promise.all([grab(lightRef), grab(darkRef)]);

        return { light, dark };
    }, [viewShot]);

    useEffect(() => {
        registerChartCapturer(capture);

        return () => unregisterChartCapturer();
    }, [capture]);

    // No native view-shot in this binary → render nothing (the widget simply
    // ships without a chart image) rather than mounting an undefined component.
    if (!viewShot) {
        return null;
    }

    const { ViewShot } = viewShot;

    const offscreen = {
        position: 'absolute',
        left: -10000,
        top: -10000,
        width: WIDGET_CHART_WIDTH,
        height: WIDGET_CHART_HEIGHT,
    };

    return (
        <View pointerEvents="none" style={offscreen} accessible={false} importantForAccessibility="no-hide-descendants">
            <ViewShot ref={lightRef} options={{ format: 'png', result: 'base64' }}>
                <WidgetChartCanvas series={series} forceDark={false} />
            </ViewShot>
            <ViewShot ref={darkRef} options={{ format: 'png', result: 'base64' }}>
                <WidgetChartCanvas series={series} forceDark />
            </ViewShot>
        </View>
    );
};

export default WidgetChartHost;
